import ReactJsonView from '@microlink/react-json-view'
import { Loader2, Play, RotateCcw } from 'lucide-react'
import { type FC } from 'react'

import TaskNotes from 'components/reusable/TaskNotes'
import { Label } from 'components/ui/label'
import { Switch } from 'components/ui/switch'
import { Badge, Button, CollapsibleContainer } from 'slab'
import { getFormattedDate } from 'utils'
import { type BrokersTaskDetail } from 'utils/web_api.schemas'

import AuditHistoryDrawer from './AuditHistoryDrawer'
import TaskResultExplorer from './TaskResultExplorer'
import { type useTaskAdminActions } from './useTaskAdminActions'
import { type useTaskMetadata } from './useTaskMetadata'

export const PlaywrightExecutionSection: FC<{
  task: BrokersTaskDetail
  taskId: string
  adminActions: ReturnType<typeof useTaskAdminActions>
}> = ({ task, taskId, adminActions }) => {
  const hasPlaywrightExecution = Boolean(task.playwright_execution_key)
  if (!hasPlaywrightExecution) return null

  return (
    <div className='rounded-lg border shadow-sm bg-white p-5'>
      <div className='flex flex-wrap items-center justify-between gap-4'>
        <div>
          <h3 className='text-sm font-semibold text-gray-900'>Playwright execution</h3>
          <p className='mt-1 text-xs text-gray-500'>
            Generate a local command to replay this task via automation.
          </p>
        </div>
        <Badge type='neutral'>{task.playwright_execution_key}</Badge>
      </div>

      <div className='mt-4 grid gap-3 md:grid-cols-3 md:items-end'>
        <div className='flex items-center justify-between rounded border border-gray-200 bg-gray-50 p-3'>
          <Label htmlFor={`playwright-safety-${taskId}`} className='text-sm font-medium text-gray-800'>
            Safety checks
          </Label>
          <Switch
            id={`playwright-safety-${taskId}`}
            checked={adminActions.includeSafety}
            onCheckedChange={(checked) => adminActions.setIncludeSafety(checked)}
          />
        </div>
        <div className='flex items-center justify-between rounded border border-gray-200 bg-gray-50 p-3'>
          <Label htmlFor={`playwright-ignore-hold-${taskId}`} className='text-sm font-medium text-gray-800'>
            Ignore hold
          </Label>
          <Switch
            id={`playwright-ignore-hold-${taskId}`}
            checked={adminActions.ignoreHold}
            onCheckedChange={(checked) => adminActions.setIgnoreHold(checked)}
          />
        </div>
      </div>

      <div className='mt-4'>
        <Button
          type='button'
          onClick={adminActions.handleCopyPlaywrightCommand}
          disabled={adminActions.buildPlaywrightCommandMutation.isPending}
        >
          {adminActions.buildPlaywrightCommandMutation.isPending
            ? <Loader2 className='h-4 w-4 animate-spin' />
            : 'Copy Playwright command'}
        </Button>
      </div>
    </div>
  )
}

export const TaskActionsSection: FC<{
  task: BrokersTaskDetail
  adminActions: ReturnType<typeof useTaskAdminActions>
  headingLevel?: 3 | 4
}> = ({ task, adminActions, headingLevel = 4 }) => {
  const hasLease = Boolean(task.lease_holder || task.lease_expires_at)

  return (
    <CollapsibleContainer heading='Task actions' headingLevel={headingLevel} defaultOpen={false}>
      <div className='space-y-6 p-4'>
        <div className='space-y-2'>
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <p className='text-sm text-gray-600'>
              Review and clear the current lease when automation is stuck.
            </p>
            <Badge type={task.lease_active ? 'warning' : 'neutral'}>
              {task.lease_active ? 'Lease active' : 'No active lease'}
            </Badge>
          </div>
          <div className='rounded border border-gray-100 bg-gray-50 p-3 text-xs text-gray-600'>
            <div className='flex items-center justify-between gap-2'>
              <span className='font-medium text-gray-800'>Lease holder</span>
              <span className='truncate'>
                {task.lease_holder_name ?? (task.lease_holder ? 'Unknown executor' : 'None')}
              </span>
            </div>
            {task.lease_holder && (
              <div className='mt-1 truncate font-mono text-[11px] text-gray-500'>
                {task.lease_holder}
              </div>
            )}
            <div className='mt-2 flex items-center justify-between gap-2'>
              <span className='font-medium text-gray-800'>Expires</span>
              <span>
                {task.lease_expires_at
                  ? getFormattedDate(task.lease_expires_at, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })
                  : 'Not set'}
              </span>
            </div>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <Button
              type='button'
              variant='secondary'
              onClick={() => adminActions.handleClearLease()}
              disabled={!hasLease || adminActions.clearLeaseMutation.isPending}
            >
              <span className='flex items-center gap-1'>
                {adminActions.clearLeaseMutation.isPending
                  ? <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  : <RotateCcw className='mr-2 h-4 w-4' />}
                Clear lease
              </span>
            </Button>
            {task.lease_holder_url && (
              <Button
                type='button'
                variant='tertiary'
                size='small'
                onClick={() =>
                  window.open(task.lease_holder_url!, '_blank', 'noopener,noreferrer')}
              >
                Open GoodJob details
              </Button>
            )}
          </div>
          {!hasLease && (
            <p className='text-xs text-gray-500'>No lease is currently held on this task.</p>
          )}
        </div>

        <div className='space-y-2'>
          <p className='text-sm text-gray-600'>
            Reset the task back to a pending state so it can be retried.
          </p>
          {task.status === 'processing' && (
            <Button
              type='button'
              variant='primary-destructive'
              onClick={adminActions.handleFailTask}
              disabled={adminActions.bulkUpdateMutation.isPending}
            >
              <span className='flex items-center gap-1'>
                {adminActions.bulkUpdateMutation.isPending
                  ? <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  : <RotateCcw className='mr-2 h-4 w-4' />}
                Move task to failed state
              </span>
            </Button>
          )}
          {task.status !== 'processing' && (
            <Button
              type='button'
              variant='secondary'
              onClick={adminActions.handleResetToPending}
              disabled={adminActions.resetTaskMutation.isPending}
            >
              <span className='flex items-center gap-1'>
                {adminActions.resetTaskMutation.isPending
                  ? <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  : <RotateCcw className='mr-2 h-4 w-4' />}
                Reset to pending
              </span>
            </Button>
          )}
        </div>

        <div className='space-y-2'>
          <p className='text-sm text-gray-600'>
            Queue this task for automated execution. The task must be pending, automated, and ready
            to run without blockers.
          </p>
          <Button
            type='button'
            onClick={adminActions.handleExecuteTask}
            disabled={
              !task.can_execute ||
              task.execution_method !== 'automated' ||
              adminActions.executeTaskMutation.isPending
            }
          >
            <span className='flex items-center gap-1'>
              {adminActions.executeTaskMutation.isPending
                ? <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                : <Play className='mr-2 h-4 w-4' />}
              Run automated task
            </span>
          </Button>
          {(!task.can_execute || task.execution_method !== 'automated') && (
            <p className='text-xs text-gray-500'>
              Task must be pending, automated, and free of blocking dependencies before it can run.
            </p>
          )}
        </div>
      </div>
    </CollapsibleContainer>
  )
}

export const RunbookInstructionsSection: FC<{
  metadata: ReturnType<typeof useTaskMetadata>
  headingLevel?: 3 | 4
}> = ({ metadata, headingLevel = 4 }) => {
  if (!metadata.instructions && !metadata.scribeUrl && !metadata.runbookUrl) {
    return null
  }

  return (
    <CollapsibleContainer
      heading='Runbook & Instructions'
      headingLevel={headingLevel}
      defaultOpen={false}
    >
      <div className='space-y-3 bg-gray-50 p-4'>
        {metadata.instructions && (
          <p className='whitespace-pre-line text-sm text-gray-700'>{metadata.instructions}</p>
        )}
        <div className='flex flex-wrap gap-2 text-sm'>
          {metadata.runbookUrl && (
            <a
              href={metadata.runbookUrl}
              target='_blank'
              rel='noreferrer'
              className='text-blue-600 hover:text-blue-500'
            >
              Open runbook
            </a>
          )}
          {metadata.scribeUrl && (
            <a
              href={metadata.scribeUrl}
              target='_blank'
              rel='noreferrer'
              className='text-blue-600 hover:text-blue-500'
            >
              View scribe walkthrough
            </a>
          )}
        </div>
      </div>
    </CollapsibleContainer>
  )
}

export const TaskMetadataSection: FC<{
  task: BrokersTaskDetail
  headingLevel?: 3 | 4
}> = ({ task, headingLevel = 4 }) => {
  if (!task.metadata || Object.keys(task.metadata).length === 0) {
    return null
  }

  return (
    <CollapsibleContainer heading='Task metadata' headingLevel={headingLevel} defaultOpen={false}>
      <div className='p-4'>
        <ReactJsonView
          src={task.metadata as Record<string, unknown>}
          theme={'github' as any}
          collapsed={2}
          displayDataTypes={false}
          displayObjectSize={false}
          enableClipboard={false}
        />
      </div>
    </CollapsibleContainer>
  )
}

export const ExecutionHistorySection: FC<{
  taskId: string
  adminActions: ReturnType<typeof useTaskAdminActions>
  headingLevel?: 3 | 4
}> = ({ taskId, adminActions, headingLevel = 4 }) => {
  return (
    <CollapsibleContainer
      heading='Execution history'
      headingLevel={headingLevel}
      defaultOpen={false}
      onOpenChange={(open) => {
        if (open) {
          adminActions.setExecutionHistoryVersion((previous) => previous + 1)
        }
      }}
    >
      <div className='p-4'>
        <TaskResultExplorer
          taskId={taskId}
          collapseVersion={adminActions.executionHistoryVersion}
          results={adminActions.taskResults}
          isLoading={adminActions.taskResultsQuery.isLoading}
        />
      </div>
    </CollapsibleContainer>
  )
}

export const TaskNotesSection: FC<{
  task: BrokersTaskDetail
  headingLevel?: 3 | 4
}> = ({ task, headingLevel = 4 }) => {
  return (
    <CollapsibleContainer heading='Task notes' headingLevel={headingLevel} defaultOpen={false}>
      <div className='p-4'>
        <TaskNotes task={task} displayExistingNotes />
      </div>
    </CollapsibleContainer>
  )
}

export const AdminAuditHistorySection: FC<{
  task: BrokersTaskDetail
  auditSectionOpen: boolean
  setAuditSectionOpen: (open: boolean) => void
  formatTaskType?: (type: string) => string
  headingLevel?: 3 | 4
}> = ({ task, auditSectionOpen, setAuditSectionOpen, formatTaskType, headingLevel = 4 }) => {
  const formatTaskTypeDefault = (type: string) => {
    return type
      .replace(/Task$/, '')
      .replace(/([A-Z])/g, ' $1')
      .trim()
  }

  return (
    <CollapsibleContainer
      heading='Task audit history'
      headingLevel={headingLevel}
      defaultOpen={false}
      onOpenChange={setAuditSectionOpen}
    >
      <div className='p-4'>
        <AuditHistoryDrawer
          params={{
            auditable_type: 'Tasks::Task',
            auditable_id: task.id,
          }}
          isOpen={auditSectionOpen}
          title={`Task Audit History - ${task.display_name || (formatTaskType || formatTaskTypeDefault)(task.task_type || '')}`}
          containerClasses='px-0'
        />
      </div>
    </CollapsibleContainer>
  )
}
